// =============================================================
// Sunsets and Dinks - global theme behavior.
// Cart drawer, mobile drawer, search overlay, cart updates,
// scroll reveal, parallax, free-ship bar.
// =============================================================
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  const moneyFormat = window.shopMoneyFormat || '${{amount}}';
  function formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    if (moneyFormat.includes('amount_no_decimals')) {
      return moneyFormat.replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString());
    }
    return moneyFormat.replace(/\{\{\s*amount(?:_with_comma_separator)?\s*\}\}/, value);
  }

  // -----------------------------------------------------------
  // Scroll reveal
  // -----------------------------------------------------------
  function initReveal() {
    const els = $$('.reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => io.observe(el));
  }

  // -----------------------------------------------------------
  // Parallax (hero photo, lifestyle photo)
  // -----------------------------------------------------------
  function initParallax() {
    const targets = $$('[data-parallax]');
    if (!targets.length) return;
    let raf = 0;
    const update = () => {
      const vh = window.innerHeight;
      targets.forEach((el) => {
        const factor = parseFloat(el.dataset.parallax) || 0.08;
        const r = el.getBoundingClientRect();
        const dist = r.top + r.height / 2 - vh / 2;
        el.style.transform = `translate3d(0, ${(-dist * factor).toFixed(1)}px, 0)`;
      });
      raf = 0;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    on(window, 'scroll', onScroll, { passive: true });
    on(window, 'resize', onScroll, { passive: true });
  }

  // -----------------------------------------------------------
  // Drawer base
  // -----------------------------------------------------------
  function bindDrawer(toggleSel, drawerSel, closeSel) {
    const toggle = $(toggleSel);
    const drawer = $(drawerSel);
    if (!drawer) return;
    const closes = closeSel ? $$(closeSel, drawer) : [];

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.removeAttribute('inert');
      document.body.classList.add('drawer-open');
      const focusable = drawer.querySelector('[data-drawer-autofocus], button, a, input');
      if (focusable) focusable.focus();
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      document.body.classList.remove('drawer-open');
      if (toggle) toggle.focus();
    }
    function toggleOpen(e) {
      if (e) e.preventDefault();
      if (drawer.classList.contains('is-open')) close(); else open();
    }

    if (toggle) on(toggle, 'click', toggleOpen);
    closes.forEach((b) => on(b, 'click', close));
    on(document, 'keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
    on(drawer, 'click', (e) => { if (e.target === drawer) close(); });

    drawer.__open = open;
    drawer.__close = close;
    return drawer;
  }

  // -----------------------------------------------------------
  // Cart - fetch and render drawer
  // -----------------------------------------------------------
  async function fetchCart() {
    const r = await fetch(window.routes.cart_url + '.js', { headers: { 'Accept': 'application/json' } });
    return r.json();
  }

  async function changeLine(line, quantity) {
    const r = await fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    return r.json();
  }

  function updateCartCount(count) {
    window.cartCount = count;
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.dataset.count = count;
      if (count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
    // The pattern updater only writes to elements that ALSO have a child .hdr__cart-text;
    // this keeps the desktop pill ("Cart · 3") in sync without nuking icon-mode markup.
    $$('[data-cart-count-pattern]').forEach((el) => {
      const tpl = el.dataset.cartCountPattern || 'Cart · COUNT';
      const textNode = el.querySelector('.hdr__cart-text');
      if (textNode) {
        textNode.textContent = tpl.replace('COUNT', count);
      } else {
        el.textContent = tpl.replace('COUNT', count);
      }
    });
  }

  function updateShipBar(subtotalCents) {
    const min = (window.shopFreeShipMin || 75) * 100;
    const remaining = Math.max(0, min - subtotalCents);
    const pct = Math.min(100, (subtotalCents / min) * 100);
    $$('[data-shipbar-fill]').forEach((el) => {
      el.style.width = pct + '%';
      el.classList.toggle('is-full', remaining === 0);
    });
    $$('[data-shipbar-note]').forEach((el) => {
      el.classList.toggle('is-free', remaining === 0);
      if (remaining === 0) {
        el.innerHTML = '★ <strong>Free Shipping Unlocked.</strong> Nice work.';
      } else {
        el.innerHTML = 'Add <strong>' + formatMoney(remaining) + '</strong> more for free shipping.';
      }
    });
  }

  function renderCartDrawer(cart) {
    const drawer = $('#CartDrawer');
    if (!drawer) return;
    const linesWrap = drawer.querySelector('[data-cart-lines]');
    const empty = drawer.querySelector('[data-cart-empty]');
    const shippingBlock = drawer.querySelector('[data-cart-shipping-block]');
    if (cart.item_count === 0) {
      if (linesWrap) linesWrap.innerHTML = '';
      if (empty) empty.hidden = false;
      if (shippingBlock) shippingBlock.hidden = true;   // hide progress bar when empty
    } else {
      if (empty) empty.hidden = true;
      if (shippingBlock) shippingBlock.hidden = false;
      if (linesWrap) {
        linesWrap.innerHTML = cart.items.map((it, idx) => {
          // /cart.js returns featured_image: {url, alt, ...}; image: also a URL string
          // The url has no protocol prefix sometimes (//cdn...) — leave as-is.
          let imgUrl = (it.featured_image && it.featured_image.url) || it.image || null;
          // Add a sized variant if Shopify CDN URL — insert _200x before extension
          if (imgUrl && /cdn\.shopify\.com/.test(imgUrl) && !/_\d+x(\d+x)?\./.test(imgUrl)) {
            imgUrl = imgUrl.replace(/(\.(?:jpe?g|png|webp|gif))(\?|$)/i, '_200x$1$2');
          }
          const imgAlt = (it.featured_image && it.featured_image.alt) || it.product_title;
          // Build options string, filtering out single-variant placeholder ("Default Title")
          const optsStr = (it.options_with_values || [])
            .filter((o) => o && o.value && o.value !== 'Default Title')
            .map((o) => o.value)
            .join(' · ');
          // 3-way fallback for title — Shopify cart.js inconsistencies on dev stores
          // sometimes leave product_title undefined; fall back to product.title or top-level title
          const lineTitle =
            it.product_title ||
            (it.product && it.product.title) ||
            (it.title || '').replace(/ - Default Title$/, '') ||
            'Item';
          const safeTitle = String(lineTitle).replace(/[<>&"]/g, (c) =>
            ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'}[c])
          );
          return `
          <div class="line" data-line-index="${idx + 1}" data-line-key="${it.key}">
            <div class="line__media">
              ${imgUrl
                ? `<img src="${imgUrl}" alt="${imgAlt}" loading="lazy">`
                : `<div class="line__media-ph">${safeTitle.toUpperCase()}</div>`}
            </div>
            <div class="line__info">
              <a class="line__name" href="${it.url || '#'}">${safeTitle}</a>
              ${optsStr ? `<div class="line__opts">${optsStr}</div>` : ''}
              <div class="line__bottomrow">
                <div class="qty">
                  <button data-qty-decr aria-label="Decrease">−</button>
                  <span data-qty>${it.quantity}</span>
                  <button data-qty-incr aria-label="Increase">+</button>
                </div>
                <button class="line__remove" data-line-remove>Remove</button>
              </div>
            </div>
            <div class="line__price">${formatMoney(it.final_line_price)}</div>
          </div>
        `;
        }).join('');
      }
    }
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    // Update the drawer header item count ("3 items" / "1 item")
    const drawerCount = drawer.querySelector('[data-cart-drawer-count]');
    if (drawerCount) drawerCount.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items');
    updateShipBar(cart.total_price);
    updateCartCount(cart.item_count);
  }

  function bindCartLineEvents(root) {
    on(root, 'click', async (e) => {
      const line = e.target.closest('[data-line-index]');
      if (!line) return;
      const idx = parseInt(line.dataset.lineIndex, 10);
      const qtyEl = line.querySelector('[data-qty]');
      const current = parseInt(qtyEl.textContent, 10) || 0;
      let next = current;
      if (e.target.matches('[data-qty-incr]'))      next = current + 1;
      else if (e.target.matches('[data-qty-decr]')) next = Math.max(0, current - 1);
      else if (e.target.matches('[data-line-remove]')) next = 0;
      else return;
      e.preventDefault();
      const cart = await changeLine(idx, next);
      renderCartDrawer(cart);
      // Also reload /cart page if we are on it - simpler than diff-rendering the lines.
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
      }
    });
  }

  // -----------------------------------------------------------
  // Wire add-to-cart forms
  // -----------------------------------------------------------
  function bindAtcForms() {
    $$('[data-atc-form]').forEach((form) => {
      on(form, 'submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[type=submit]');
        if (btn) { btn.disabled = true; btn.dataset.originalText = btn.textContent; btn.textContent = 'Adding…'; }
        const data = new FormData(form);
        try {
          const r = await fetch(window.routes.cart_add_url + '.js', {
            method: 'POST',
            body: data,
            headers: { 'Accept': 'application/json' }
          });
          if (!r.ok) throw new Error('atc failed');
          const cart = await fetchCart();
          renderCartDrawer(cart);
          const drawer = $('#CartDrawer');
          if (drawer && drawer.__open) drawer.__open();
          if (btn) { btn.textContent = btn.dataset.originalText || 'Add to Cart'; btn.disabled = false; }
        } catch (err) {
          console.error(err);
          if (btn) { btn.textContent = 'Try again'; btn.disabled = false; }
        }
      });
    });
  }

  // -----------------------------------------------------------
  // Predictive search overlay
  // -----------------------------------------------------------
  function initSearch() {
    const overlay = $('#SearchOverlay');
    if (!overlay) return;
    const input = overlay.querySelector('input[type=search]');
    const results = overlay.querySelector('[data-search-results]');
    let timer = 0;
    on(input, 'input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        // /search/suggest.json returns predictive products as JSON — no theme section needed.
        const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                    '&resources[type]=product&resources[limit]=8' +
                    '&resources[options][unavailable_products]=last';
        try {
          const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
          const data = await r.json();
          const products = (((data || {}).resources || {}).results || {}).products || [];
          if (products.length === 0) {
            results.innerHTML = `<p class="muted">No matches for "${q}".</p>`;
            return;
          }
          results.innerHTML = '<div class="search-results__grid">' +
            products.map((p) => {
              let imgUrl = p.image || (p.featured_image && p.featured_image.url) || '';
              if (imgUrl && /cdn\.shopify\.com/.test(imgUrl) && !/_\d+x(\d+x)?\./.test(imgUrl)) {
                imgUrl = imgUrl.replace(/(\.(?:jpe?g|png|webp|gif))(\?|$)/i, '_200x$1$2');
              }
              return `
                <a class="search-result" href="${p.url}">
                  ${imgUrl ? `<img src="${imgUrl}" alt="${(p.title || '').replace(/"/g,'&quot;')}" loading="lazy">` : '<div class="search-result__ph"></div>'}
                  <div class="search-result__info">
                    <div class="search-result__title">${p.title || ''}</div>
                    <div class="search-result__price">${p.price || ''}</div>
                  </div>
                </a>
              `;
            }).join('') + '</div>';
        } catch (e) {
          results.innerHTML = '<p class="muted">Search unavailable. Try again.</p>';
        }
      }, 180);
    });
  }

  // -----------------------------------------------------------
  // Boot
  // -----------------------------------------------------------
  function boot() {
    initReveal();
    initParallax();
    bindDrawer('[data-cart-toggle]', '#CartDrawer', '[data-drawer-close]');
    bindDrawer('[data-mobile-toggle]', '#MobileDrawer', '[data-drawer-close]');
    bindDrawer('[data-search-toggle]', '#SearchOverlay', '[data-drawer-close]');
    bindCartLineEvents(document);
    bindAtcForms();
    initSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
